<template>
  <div>
    <div class="pb-2 text-xs-left" :class="[$style.headingText]">
      {{ lang.social_media_image_title }}
    </div>
    <div>{{ lang.social_media_image_description }}</div>
    <div :class="$style.wrapInfoForm">
      <v-img
        v-if="croppedSocialMediaImageUrl"
        :src="croppedSocialMediaImageUrl"
        :aspect-ratio="2 / 1"
        :class="$style.snsImg"
      ></v-img>
      <v-responsive v-else :aspect-ratio="2 / 1" :class="$style.snsImg">
        <v-layout align-center justify-center fill-height>
          <v-progress-circular
            indeterminate
            color="grey lighten-1"
          ></v-progress-circular>
        </v-layout>
      </v-responsive>
    </div>

    <div :class="$style.wrapInfoForm">
      <v-btn
        :class="$style.btnTextStyle"
        @click="onClickEditButton"
        :disabled="isBusy"
      >
        {{ lang.thumbnail_edit }}
      </v-btn>

      <v-btn
        @click="onClickResetButton"
        :class="$style.btnTextStyle"
        :loading="isBusy"
        :disabled="isBusy || userCroppedSocialMediaImageUrl == ''"
      >
        {{ lang.thumbnail_reset }}
      </v-btn>
    </div>

    <v-dialog
      v-model="selectThumbnailImageDialog"
      @keydown.esc="selectThumbnailImageDialog = false"
      max-width="560"
      :persistent="isBusy"
    >
      <v-card>
        <v-card-title>
          {{ lang.social_media_image_edit_dialog_title }}
        </v-card-title>

        <v-card-text>
          <vc-share-comic-cropper
            ref="socialMediaImageCropper"
            v-if="isShowCropper"
            :image-url="cropperSocialMediaImageUrl"
            :on-ready-cropper-callback="onReadySocialMediaImageCropperCallback"
            :on-error-cropper-callback="onErrorCropperCallback"
            :select-thumbnail-image-dialog="selectThumbnailImageDialog"
            :aspect-ratio="2 / 1"
            :img-style="{ 'max-width': '100%', height: '150px' }"
            :cropper-margin="80"
          ></vc-share-comic-cropper>

          <v-layout row wrap>
            <v-flex sm3>
              <v-btn
                fab
                small
                color="primary"
                @click.prevent="cropper.relativeZoom(0.1)"
                :disabled="isBusy || thumbnailImageCropperDisabled"
              >
                <v-icon>fas fa-search-plus</v-icon>
              </v-btn>

              <v-btn
                fab
                small
                color="primary"
                @click.prevent="cropper.relativeZoom(-0.1)"
                :disabled="isBusy || thumbnailImageCropperDisabled"
              >
                <v-icon>fas fa-search-minus</v-icon>
              </v-btn>
            </v-flex>

            <v-flex sm6 text-xs-center>
              <v-btn
                :class="$style.btnTextStyle"
                color="primary"
                @click="onClickSocialMediaImageFile"
                :disabled="isBusy"
              >
                {{ lang.thumbnail_edit_dialog_select_file }}
              </v-btn>

              <span v-if="selectThumbnailImageDialog">
                <input
                  type="file"
                  style="display:none;"
                  ref="socialMediaImageFileInput"
                  accept="image/png,image/jpeg"
                  @change="onChangeSocialMediaImageFileInput"
                />
              </span>
            </v-flex>
          </v-layout>
          <v-alert
            v-for="(message, key, index) in errorMessages"
            :key="index"
            :value="true"
            type="error"
          >
            {{ message }}
          </v-alert>
          <v-layout justify-end>
            <v-btn
              :class="$style.btnTextStyle"
              color="primary"
              @click="onClickSaveButton"
              :loading="isBusy"
              :disabled="isBusy || thumbnailImageCropperDisabled"
            >
              {{ lang.thumbnail_edit_dialog_apply }}
            </v-btn>

            <v-btn
              :class="$style.btnTextStyle"
              color="default"
              @click="
                socialMediaImageOriginalFileData = null;
                selectThumbnailImageDialog = false;
              "
              :disabled="isBusy"
            >
              {{ lang.thumbnail_edit_dialog_cancel }}
            </v-btn>
          </v-layout>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
const MAX_IMAGE_FILE_SIZE_HUMAN = 32;
const MAX_IMAGE_FILE_SIZE = 1024 * 1024 * MAX_IMAGE_FILE_SIZE_HUMAN;

export default {
  name: "vc-share-social-media-image",
  props: {
    originalSocialMediaImageUrl: {
      type: String,
      default: ""
    },
    croppedSocialMediaImageUrl: {
      type: String,
      default: ""
    },
    userCroppedSocialMediaImageUrl: {
      type: String,
      default: ""
    },
    postSocialMediaImage: {
      type: Function,
      default: () => new Promise(() => {})
    },
    deleteSocialMediaImage: {
      type: Function,
      default: () => new Promise(() => {})
    },
    updateUserOriginalSocialMediaImageUrl: {
      type: Function,
      default: () => new Promise(() => {})
    },
    isBusy: {
      type: Boolean,
      default: false
    },
    lang: {
      type: Object,
      default: () => ({
        social_media_image_title: "SNSシェア用画像",
        social_media_image_description:
          "Twitterに投稿した際に使用される画像です。Twitterでの表示に推奨されている2:1の比率になっています。",
        thumbnail_edit: "変更",
        thumbnail_reset: "リセット",
        social_media_image_edit_dialog_title: "SNS向けサムネイル画像設定",
        thumbnail_edit_dialog_select_file: "ファイルを選択",
        thumbnail_edit_dialog_apply: "設定",
        thumbnail_edit_dialog_cancel: "キャンセル",
        thumbnail_edit_error_image_size:
          ":filename : :max_size MB より小さい画像を選択してください。",
        thumbnail_edit_error_file_type:
          ":filename : jpeg もしくは png の画像を選択してください。",
        thumbnail_edit_error_read_image_failure:
          "画像の読み込みに失敗しました。"
      })
    },
    updateDefaultOriginalSocialMediaImageUrlQueryString: {
      type: Function,
      default: () => {
        return {};
      }
    }
  },
  data: () => ({
    socialMediaImageFileData: null,
    selectThumbnailImageDialog: false,
    isShowCropper: false,
    thumbnailImageCropperDisabled: false,
    cropperSocialMediaImageUrl: "",
    onReadySocialMediaImageCropperCallback: () => {},
    onErrorCropperCallback: () => {},
    cropper: null,
    canvasToBlob: async () => {},
    errorMessages: []
  }),
  methods: {
    onClickEditButton() {
      this.errorMessages = [];
      this.updateDefaultOriginalSocialMediaImageUrlQueryString();

      if (this.cropper) {
        this.cropper.enable();
      }
      if (!this.originalSocialMediaImageUrl) {
        this.setCropperToBlankImage();
      } else {
        this.socialMediaImageFileData = this.originalSocialMediaImageUrl;
        this.thumbnailImageCropperDisabled = false;
        this.cropperSocialMediaImageUrl = this.socialMediaImageFileData;
        this.onReadySocialMediaImageCropperCallback = (
          cropper,
          croppedCanvasToBlob
        ) => {
          this.cropper = cropper;
          this.cropper.enable();
          this.canvasToBlob = croppedCanvasToBlob;
        };
        this.onErrorCropperCallback = () => {
          this.setCropperToBlankImage();
          this.errorMessages.push(
            this.lang.thumbnail_edit_error_read_image_failure
          );
        };
      }

      this.$nextTick(() => {
        this.selectThumbnailImageDialog = true;
        this.isShowCropper = true;
      });
    },
    setCropperToBlankImage() {
      // cropper に空白画像を設定し、disabled にする
      const canvas = document.createElement("canvas");
      canvas.width = 500;
      canvas.height = 500;
      this.socialMediaImageFileData = canvas.toDataURL();
      this.thumbnailImageCropperDisabled = true;
      this.cropperSocialMediaImageUrl = this.socialMediaImageFileData;
      this.onReadySocialMediaImageCropperCallback = (
        cropper,
        croppedCanvasToBlob
      ) => {
        this.cropper = cropper;
        this.cropper.disable();
        this.canvasToBlob = croppedCanvasToBlob;
      };
    },
    onClickResetButton() {
      this.deleteSocialMediaImage().then(() => {
        this.updateUserOriginalSocialMediaImageUrl("");
        this.isShowCropper = false;
      });
    },
    onClickSocialMediaImageFile() {
      this.$refs.socialMediaImageFileInput.click();
    },
    onChangeSocialMediaImageFileInput(e) {
      const files = e.target.files;
      this.errorMessages = [];

      if (files[0] == undefined) {
        return;
      }

      const file = files[0];

      if (file.type !== "image/jpeg" && file.type !== "image/png") {
        const message = this.lang.thumbnail_edit_error_file_type.replace(
          ":filename",
          file.name
        );
        this.errorMessages.push(message);
        return;
      }

      if (file.size > MAX_IMAGE_FILE_SIZE) {
        const message = this.lang.thumbnail_edit_error_image_size
          .replace(":filename", file.name)
          .replace(":max_size", MAX_IMAGE_FILE_SIZE_HUMAN);
        this.errorMessages.push(message);
        return;
      }

      const fr = new FileReader();
      fr.readAsDataURL(file);
      fr.onload = () => {
        const imageUrl = fr.result;
        this.socialMediaImageFileData = imageUrl;
        this.thumbnailImageCropperDisabled = false;
        if (this.cropper) {
          this.cropper.enable();
        }
        this.cropperSocialMediaImageUrl = this.socialMediaImageFileData;
        this.onReadySocialMediaImageCropperCallback = () => {};
      };
    },
    onClickSaveButton() {
      this.updateUserOriginalSocialMediaImageUrl(this.socialMediaImageFileData);
      this.canvasToBlob()
        .then(blob => this.postSocialMediaImage(blob))
        .then(() => {
          this.socialMediaImageFileData = null;
          this.selectThumbnailImageDialog = false;
        });
    }
  }
};
</script>

<style module>
.btnTextStyle {
  text-transform: none !important;
}
.wrapInfoForm {
  display: flex;
  justify-content: center;
}
.snsImg {
  width: 40%;
  max-width: 40%;
  height: auto;
  border: 1px solid lightgray;
}
.headingText {
  font-weight: bold;
  font-size: 16px;
}
</style>
